import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  ButtonGroup,
  Snackbar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
  Skeleton,
} from '@mui/material';
import {
  IconArrowLeft,
  IconTrash,
  IconExternalLink,
  IconSend,
  IconChecks,
  IconWand,
  IconPrinter,
  IconCreditCard,
  IconRefresh,
  IconUserEdit,
} from '@tabler/icons-react';
import { DateField } from '../components/dropdown';
import { FormField } from '../components/panel/FormField';
import {
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
} from '../components/table';
import type { InvoiceDetail, InvoiceLineItemCreate } from '@techgitter/contracts';
import {
  fetchInvoiceDetail,
  updateInvoiceMetadata,
  addInvoiceLineItem,
  updateInvoiceLineItem,
  deleteInvoiceLineItem,
  deleteInvoice,
  printInvoice,
  syncInvoiceFromRs,
} from '../services/invoicesApi';
import {
  enrichInvoice,
  markInvoiceSent,
  sendInvoiceEmail,
} from '../services/xeroIntegrationService';
import type { EnrichmentFields } from '../services/xeroIntegrationService';
import { InvoiceLineItemEditor } from '../components/invoices/InvoiceLineItemEditor';
import { Chip } from '../components/chip';
import type { ChipTone } from '../components/chip';
import { fetchPayments } from '../services/paymentsApi';
import { printInvoiceDocument } from '../services/printApi';
import { printFeedback } from '../utils/printFeedback';
import type { PaymentsListItem } from '../services/paymentsApi';
import { CheckoutDialog } from '../components/payments/CheckoutDialog';
import { ChangeCustomerDialog } from '../components/invoices/ChangeCustomerDialog';
import { useSignalREvent } from '../hooks/useSignalR';
import {
  CARD_SX,
  PRIMARY_BTN_SX,
  COMPACT_BTN_SX,
  COMPACT_BTN_GROUP_SX,
} from '../styles/designSystem';
import './InvoiceDetailPage.css';

type XeroAction = 'send' | 'mark-sent' | 'enrich';

const ZERO_INVOICE_SENTINEL = 'NA_ZERO_INVOICE';

interface XeroActionGate {
  enabled: boolean;
  reason: string | null;
}

const xeroActionGate = (invoice: InvoiceDetail): XeroActionGate => {
  if (!invoice.xeroId) {
    return { enabled: false, reason: 'No Xero invoice linked' };
  }
  if (invoice.xeroId === ZERO_INVOICE_SENTINEL) {
    return { enabled: false, reason: 'Zero-value invoice — no Xero record' };
  }
  if (invoice.xeroResourceType === 'CreditNote') {
    return { enabled: false, reason: 'Credit notes not supported (Phase 2)' };
  }
  return { enabled: true, reason: null };
};

const applyEnrichment = (
  invoice: InvoiceDetail,
  fields: EnrichmentFields,
): InvoiceDetail => ({
  ...invoice,
  xeroStatus: fields.cr001_xero_status,
  xeroIsPaid: fields.cr001_xero_is_paid,
  xeroAmountDue: fields.cr001_xero_amount_due,
  xeroAmountPaid: fields.cr001_xero_amount_paid,
  xeroFullyPaidOnDate: fields.cr001_xero_fully_paid_on_date,
  xeroOnlineInvoiceUrl: fields.cr001_xero_online_invoice_url,
});

const RS_BASE_URL = 'https://techsolvers.repairshopr.com';

interface UserInfo {
  displayName: string;
  userPrincipalName: string;
  roles: string[];
}

interface InvoiceDetailPageProps {
  userInfo?: UserInfo | null;
}

const fmtAUD = (n: number): string =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function statusChipTone(status: string): ChipTone {
  const s = status.toLowerCase();
  if (s === 'paid' || s === 'tech marked paid') return 'success';
  if (s === 'outstanding' || s === 'partial') return 'warning';
  if (s === 'overdue') return 'error';
  return 'info';
}

/** Stacked label/value fact for the header strip (skill §8 L2 layout). */
const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 500,
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: 14, color: '#1a1a1a' }}>{children}</Typography>
  </Box>
);

export const InvoiceDetailPage: React.FC<InvoiceDetailPageProps> = ({ userInfo }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const invoiceNumber = location.pathname.match(/^\/invoice\/(.+)$/)?.[1] ?? '';

  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Metadata edit state
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [metadataDirty, setMetadataDirty] = useState(false);
  const [metadataSaving, setMetadataSaving] = useState(false);

  // Delete state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Change-customer dialog
  const [changeCustomerOpen, setChangeCustomerOpen] = useState(false);
  // Partial-success note when the invoice moved but its linked ticket didn't.
  const [ticketWarning, setTicketWarning] = useState<string | null>(null);

  // Print state
  const [isPrinting, setIsPrinting] = useState(false);
  // SM4RT's own A4 print (the invoice-techsolvers template, via the print
  // agent) — separate from the RepairShopr print above.
  const [isPrintingA4, setIsPrintingA4] = useState(false);

  // Refresh-from-RepairShopr state. The sync endpoint returns 202 and the DV
  // row updates out of band a few seconds later, so we re-fetch on a timer.
  const [isSyncing, setIsSyncing] = useState(false);
  const syncReloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Payments linked to this invoice
  const [payments, setPayments] = useState<PaymentsListItem[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  // Xero per-invoice action state
  const [xeroRunning, setXeroRunning] = useState<XeroAction | null>(null);
  // Also carries the A4 print's result, whose "skipped" outcome is a warning.
  const [xeroSnackbar, setXeroSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'warning' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);

  const isAdmin = userInfo?.roles.includes('admin') ?? false;

  // Pre-selected line items from ticket creation flow
  const locationState = location.state as
    | { preselectedLineItems?: InvoiceLineItemCreate[]; autoOpenCheckout?: boolean }
    | null;
  const preselectedLineItems = locationState?.preselectedLineItems;
  const autoOpenCheckout = locationState?.autoOpenCheckout === true;
  const preselectedProcessedRef = useRef(false);
  const autoCheckoutTriggeredRef = useRef(false);

  // Fetch invoice detail
  const loadInvoice = useCallback(async (signal?: AbortSignal) => {
    if (!invoiceNumber) return;
    try {
      const detail = await fetchInvoiceDetail(invoiceNumber, signal);
      setInvoice(detail);
      setDueDate(detail.dueDate ?? '');
      setNote(detail.note ?? '');
      setPoNumber(detail.poNumber ?? '');
      setMetadataDirty(false);
      return detail;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      throw err;
    }
  }, [invoiceNumber]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    loadInvoice(controller.signal)
      .catch((err) => {
        if (err?.name !== 'AbortError') {
          setError(err?.message ?? 'Failed to load invoice');
        }
      })
      .finally(() => {
        // Don't clear loading for a superseded/aborted request — its
        // finally still fires, and doing so would flash the "not found"
        // branch (loading=false, invoice=null) while the live request
        // (e.g. StrictMode's second mount) is still in flight.
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [loadInvoice]);

  // Fetch the payments that settled this invoice. Wrapped in a callback
  // so the Check Out dialog + payments:changed SignalR event can both
  // trigger a refresh without an effect re-mount.
  const loadPayments = useCallback(
    async (signal?: AbortSignal) => {
      if (!invoiceNumber) return;
      setPaymentsLoading(true);
      setPaymentsError(null);
      try {
        const res = await fetchPayments({
          page: 1,
          pageSize: 50,
          invoiceNumber,
          sort: 'createdAt',
          order: 'desc',
          signal,
        });
        setPayments(res.items);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setPaymentsError(err instanceof Error ? err.message : String(err));
      } finally {
        setPaymentsLoading(false);
      }
    },
    [invoiceNumber],
  );

  // Initial + invoice-change refetch.
  useEffect(() => {
    if (!invoiceNumber) return;
    const controller = new AbortController();
    loadPayments(controller.signal);
    return () => controller.abort();
  }, [invoiceNumber, loadPayments]);

  // Refresh both invoice (for balance) and payments list when a payment
  // is recorded — the orchestrator broadcasts `payments:changed` after
  // the RS + DV writes both succeed.
  useSignalREvent('payments:changed', (data: unknown) => {
    const payload = data as { invoiceNumber?: string } | undefined;
    if (!payload?.invoiceNumber || payload.invoiceNumber !== invoiceNumber) return;
    loadInvoice();
    loadPayments();
  });

  // Process pre-selected line items after invoice loads
  useEffect(() => {
    if (!invoice || !preselectedLineItems || preselectedProcessedRef.current) return;
    preselectedProcessedRef.current = true;

    (async () => {
      for (const item of preselectedLineItems) {
        try {
          await addInvoiceLineItem(invoiceNumber, item);
        } catch (err) {
          console.error('Failed to add pre-selected line item:', err);
        }
      }
      // Reload to get the updated state
      try {
        await loadInvoice();
        setSnackbar(`Added ${preselectedLineItems.length} line item(s) from ticket`);
      } catch {
        // ignore
      }
    })();
  }, [invoice, preselectedLineItems, invoiceNumber, loadInvoice]);

  // Auto-open the Check Out dialog when arriving from the "Save and Take
  // Payment" flow. Wait until pre-selected line items (if any) have been
  // added so the balance reflects the full ticket total.
  useEffect(() => {
    if (!autoOpenCheckout || autoCheckoutTriggeredRef.current) return;
    if (!invoice) return;
    if (preselectedLineItems && !preselectedProcessedRef.current) return;
    if (invoice.rsInvoiceId == null || invoice.rsCustomerId == null) return;
    const balance = Number(invoice.balanceDue) || 0;
    if (balance <= 0) return;
    autoCheckoutTriggeredRef.current = true;
    setCheckoutOpen(true);
  }, [autoOpenCheckout, invoice, preselectedLineItems]);

  const isReadOnly = invoice?.isPaid ?? false;

  // Metadata save
  const handleSaveMetadata = async () => {
    if (!invoice || !metadataDirty) return;
    setMetadataSaving(true);
    try {
      const updates: Record<string, string> = {};
      if (dueDate !== (invoice.dueDate ?? '')) updates.dueDate = dueDate;
      if (note !== (invoice.note ?? '')) updates.note = note;
      if (poNumber !== (invoice.poNumber ?? '')) updates.poNumber = poNumber;
      if (Object.keys(updates).length === 0) return;

      const updated = await updateInvoiceMetadata(invoiceNumber, updates);
      setInvoice(updated);
      setMetadataDirty(false);
      setSnackbar('Invoice updated');
    } catch (err) {
      setSnackbar(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setMetadataSaving(false);
    }
  };

  // Line item handlers
  const handleAddLineItem = async (item: {
    name: string;
    description: string;
    quantity: number;
    price: number;
    discountPercent: number;
    productId?: number;
  }) => {
    const updated = await addInvoiceLineItem(invoiceNumber, item);
    setInvoice(updated);
  };

  const handleUpdateLineItem = async (
    lineItemId: number,
    updates: { description?: string; quantity?: number; price?: number; discountPercent?: number },
  ) => {
    const updated = await updateInvoiceLineItem(invoiceNumber, lineItemId, updates);
    setInvoice(updated);
  };

  const handleDeleteLineItem = async (lineItemId: number) => {
    await deleteInvoiceLineItem(invoiceNumber, lineItemId);
    // Reload since delete returns void
    await loadInvoice();
  };

  const runXeroAction = useCallback(
    async (action: XeroAction) => {
      if (!invoice) return;
      setXeroRunning(action);
      try {
        if (action === 'send') {
          await sendInvoiceEmail(invoice.number);
          setXeroSnackbar({
            open: true,
            message: `Sent invoice ${invoice.number} via Xero`,
            severity: 'success',
          });
        } else if (action === 'mark-sent') {
          await markInvoiceSent(invoice.number);
          setXeroSnackbar({
            open: true,
            message: `Marked invoice ${invoice.number} as sent in Xero`,
            severity: 'success',
          });
        } else {
          const result = await enrichInvoice(invoice.number);
          setInvoice((current) =>
            current ? applyEnrichment(current, result.enriched_fields) : current,
          );
          setXeroSnackbar({
            open: true,
            message: `Enriched invoice ${invoice.number} from Xero`,
            severity: 'success',
          });
        }
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : `Xero ${action} failed for invoice ${invoice.number}`;
        setXeroSnackbar({ open: true, message, severity: 'error' });
      } finally {
        setXeroRunning(null);
      }
    },
    [invoice],
  );

  const handleDeleteInvoice = async () => {
    setIsDeleting(true);
    try {
      await deleteInvoice(invoiceNumber);
      setDeleteConfirmOpen(false);
      navigate(-1);
    } catch (err) {
      setSnackbar(err instanceof Error ? err.message : 'Failed to delete invoice');
      setIsDeleting(false);
    }
  };

  const handlePrintInvoice = async () => {
    setIsPrinting(true);
    try {
      await printInvoice(invoiceNumber);
      setSnackbar('Invoice print queued in RepairShopr');
    } catch (err) {
      setSnackbar(err instanceof Error ? err.message : 'Failed to print invoice');
    } finally {
      setIsPrinting(false);
    }
  };

  const handlePrintInvoiceA4 = async () => {
    setIsPrintingA4(true);
    try {
      const result = await printInvoiceDocument(invoiceNumber);
      setXeroSnackbar({ open: true, ...printFeedback(`Invoice ${invoiceNumber}`, result) });
    } catch (err) {
      setXeroSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to print invoice',
        severity: 'error',
      });
    } finally {
      setIsPrintingA4(false);
    }
  };

  const handleSyncFromRs = async () => {
    setIsSyncing(true);
    try {
      await syncInvoiceFromRs(invoiceNumber);
      setSnackbar('Refresh from RepairShopr queued — reloading shortly…');
      // The orchestrator upserts the DV row a few seconds after the 202;
      // re-fetch once it has likely completed so the user sees fresh data.
      if (syncReloadTimer.current) clearTimeout(syncReloadTimer.current);
      syncReloadTimer.current = setTimeout(() => {
        loadInvoice();
      }, 5000);
    } catch (err) {
      setSnackbar(err instanceof Error ? err.message : 'Failed to refresh invoice from RepairShopr');
    } finally {
      setIsSyncing(false);
    }
  };

  // Clear any pending refresh reload if the page unmounts first.
  useEffect(() => () => {
    if (syncReloadTimer.current) clearTimeout(syncReloadTimer.current);
  }, []);

  if (loading) {
    return (
      <Box
        className="invoice-detail"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <CircularProgress />
        <Typography sx={{ fontSize: 14, color: '#888' }}>Getting invoice…</Typography>
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Box className="invoice-detail">
        <Box className="invoice-detail__inner">
          <button type="button" className="invoice-detail__back" onClick={() => navigate(-1)}>
            <IconArrowLeft size={16} stroke={1.5} />
            Back
          </button>
          <Alert severity="error">{error || 'Invoice not found'}</Alert>
        </Box>
      </Box>
    );
  }

  const paymentsTotal = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  return (
    <Box className="invoice-detail">
      <Box className="invoice-detail__inner">
        {/* Back */}
        <button type="button" className="invoice-detail__back" onClick={() => navigate(-1)}>
          <IconArrowLeft size={16} stroke={1.5} />
          Back
        </button>

        {/* Header card */}
        <Box sx={{ ...CARD_SX, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 600, fontSize: 20, color: '#1a1a1a' }}>
              Invoice #{invoice.number}
            </Typography>
            <Chip tone={statusChipTone(invoice.status)}>{invoice.status}</Chip>
            {/* Print — SM4RT's A4 tax invoice, to the office laser */}
            <Button
              variant="outlined"
              size="small"
              sx={COMPACT_BTN_SX}
              onClick={handlePrintInvoiceA4}
              disabled={isPrintingA4}
              startIcon={isPrintingA4 ? <CircularProgress size={14} color="inherit" /> : <IconPrinter size={16} stroke={1.5} />}
            >
              Print
            </Button>
            {/* External links — RepairShopr / Xero / Print in RS */}
            {(invoice.rsInvoiceId || invoice.xeroId) && (
              <ButtonGroup variant="outlined" size="small" sx={COMPACT_BTN_GROUP_SX}>
                {invoice.rsInvoiceId && (
                  <Button
                    onClick={() =>
                      window.open(
                        `${RS_BASE_URL}/invoices/${invoice.rsInvoiceId}`,
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }
                    startIcon={<IconExternalLink size={16} stroke={1.5} />}
                  >
                    RS
                  </Button>
                )}
                {invoice.xeroId && (
                  <Button
                    onClick={() =>
                      window.open(
                        `https://go.xero.com/app/!M-3nX/invoicing/view/${invoice.xeroId}`,
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }
                    startIcon={<IconExternalLink size={16} stroke={1.5} />}
                  >
                    Xero
                  </Button>
                )}
                {invoice.rsInvoiceId && (
                  <Button
                    onClick={handlePrintInvoice}
                    disabled={isPrinting}
                    startIcon={isPrinting ? <CircularProgress size={14} color="inherit" /> : <IconPrinter size={16} stroke={1.5} />}
                  >
                    Print in RS
                  </Button>
                )}
                {invoice.rsInvoiceId && (
                  <Button
                    onClick={handleSyncFromRs}
                    disabled={isSyncing}
                    startIcon={isSyncing ? <CircularProgress size={14} color="inherit" /> : <IconRefresh size={16} stroke={1.5} />}
                  >
                    Refresh from RS
                  </Button>
                )}
              </ButtonGroup>
            )}
            {/* Xero workflow — Send / Mark Sent / Enrich */}
            {(() => {
              const gate = xeroActionGate(invoice);
              const actionBtn = (
                action: XeroAction,
                label: string,
                Icon: React.ElementType,
                onClick: () => void,
              ) => {
                const isRunning = xeroRunning === action;
                const isDisabled = !gate.enabled || (xeroRunning !== null && !isRunning);
                return (
                  <Button
                    key={action}
                    disabled={isDisabled}
                    onClick={() => {
                      if (!gate.enabled) return;
                      onClick();
                    }}
                    startIcon={
                      isRunning
                        ? <CircularProgress size={14} color="inherit" />
                        : <Icon size={16} stroke={1.5} />
                    }
                  >
                    {label}
                  </Button>
                );
              };
              const group = (
                <ButtonGroup variant="outlined" size="small" sx={COMPACT_BTN_GROUP_SX}>
                  {actionBtn('send', 'Send via Xero', IconSend, () => setConfirmSendOpen(true))}
                  {actionBtn('mark-sent', 'Mark Sent', IconChecks, () => runXeroAction('mark-sent'))}
                  {actionBtn('enrich', 'Enrich', IconWand, () => runXeroAction('enrich'))}
                </ButtonGroup>
              );
              return gate.enabled ? group : (
                <Tooltip title={gate.reason ?? ''}>
                  <span>{group}</span>
                </Tooltip>
              );
            })()}
            {/* Internal nav links — kept at the end */}
            {invoice.rsTicketNumber && (
              <Button
                variant="outlined"
                size="small"
                sx={COMPACT_BTN_SX}
                onClick={() => navigate(`/ticket/${invoice.rsTicketNumber}`)}
              >
                Ticket #{invoice.rsTicketNumber}
              </Button>
            )}
            {invoice.quoteNumber && (
              <Button
                variant="outlined"
                size="small"
                sx={COMPACT_BTN_SX}
                onClick={() => navigate(`/quote/${invoice.quoteNumber}`)}
              >
                Quote #{invoice.quoteNumber}
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<IconTrash size={16} stroke={1.5} />}
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={isReadOnly}
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  py: 0.25,
                  px: 1.25,
                  minHeight: 0,
                  ml: 'auto',
                  '& .MuiButton-startIcon': { mr: 0.5 },
                }}
              >
                Delete
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 4, mt: 2, flexWrap: 'wrap' }}>
            <Fact label="Customer">
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, verticalAlign: 'middle' }}>
                <Box component="span" title={invoice.customerDesignation || undefined}>
                  {invoice.customerName || '—'}
                </Box>
                {isReadOnly ? (
                  <Tooltip title="Paid invoice — a manager must change the customer in RepairShopr">
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={COMPACT_BTN_SX}
                        disabled
                        startIcon={<IconUserEdit size={15} stroke={1.5} />}
                      >
                        Change
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={COMPACT_BTN_SX}
                    startIcon={<IconUserEdit size={15} stroke={1.5} />}
                    onClick={() => setChangeCustomerOpen(true)}
                  >
                    Change
                  </Button>
                )}
              </Box>
            </Fact>
            <Fact label="Created">{formatDate(invoice.createdAt)}</Fact>
            <Fact label="Balance Due">{fmtAUD(invoice.balanceDue)}</Fact>
          </Box>
        </Box>

        {/* Metadata */}
        <Box sx={{ ...CARD_SX, p: 2 }}>
          <div className="invoice-detail__card-title">Invoice Details</div>
          <div className="invoice-detail__field-row">
            <div style={{ width: 180 }}>
              <DateField
                label="Due Date"
                value={dueDate || null}
                onChange={(iso) => {
                  setDueDate(iso ?? '');
                  setMetadataDirty(true);
                }}
                disabled={isReadOnly}
              />
            </div>
            <div style={{ width: 180 }}>
              <FormField label="PO Number" disabled={isReadOnly}>
                <input
                  value={poNumber}
                  onChange={(e) => {
                    setPoNumber(e.target.value);
                    setMetadataDirty(true);
                  }}
                  disabled={isReadOnly}
                />
              </FormField>
            </div>
            <div className="invoice-detail__field--grow">
              <FormField label="Note" disabled={isReadOnly}>
                <input
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                    setMetadataDirty(true);
                  }}
                  disabled={isReadOnly}
                />
              </FormField>
            </div>
            {metadataDirty && !isReadOnly && (
              <Button
                variant="contained"
                onClick={handleSaveMetadata}
                disabled={metadataSaving}
                sx={{ ...PRIMARY_BTN_SX, height: 40, alignSelf: 'flex-start' }}
              >
                {metadataSaving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
              </Button>
            )}
          </div>
        </Box>

        {/* Payments linked to this invoice — mirrors the cr001_payments
            rows whose cr001_invoice_id points at this invoice. Reads are
            live (refresh on `payments:changed`); writes go through the
            Check Out dialog → /api/payments → orchestrator → RS + DV. */}
        <Box sx={{ ...CARD_SX, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1.5 }}>
            <div className="invoice-detail__card-title" style={{ marginBottom: 0 }}>
              Payments{payments.length > 0 ? ` (${payments.length})` : ''}
              {payments.length > 0 && (
                <span className="invoice-detail__muted-amount">{fmtAUD(paymentsTotal)}</span>
              )}
            </div>
            {(() => {
              const balance = Number(invoice.balanceDue) || 0;
              const canCheckOut =
                balance > 0 &&
                invoice.rsInvoiceId != null &&
                invoice.rsCustomerId != null;
              const reason = !invoice.rsInvoiceId
                ? 'No RepairShopr invoice linked'
                : !invoice.rsCustomerId
                  ? 'Customer is not linked to RepairShopr'
                  : balance <= 0
                    ? 'Invoice fully paid'
                    : '';
              return (
                <Tooltip title={canCheckOut ? 'Record a payment' : reason}>
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<IconCreditCard size={16} stroke={1.5} />}
                      disabled={!canCheckOut}
                      onClick={() => setCheckoutOpen(true)}
                      sx={PRIMARY_BTN_SX}
                    >
                      Check Out
                    </Button>
                  </span>
                </Tooltip>
              );
            })()}
          </Box>
          {paymentsError && (
            <Box sx={{ px: 2, pb: 1.5 }}>
              <Alert severity="error">Failed to load payments: {paymentsError}</Alert>
            </Box>
          )}
          {paymentsLoading ? (
            <Box sx={{ px: 2, pb: 2 }}>
              <Skeleton variant="text" width="60%" />
            </Box>
          ) : payments.length === 0 ? (
            <Box sx={{ px: 2, pb: 2 }}>
              <Typography sx={{ fontSize: 13, color: '#888' }}>
                No payments recorded against this invoice.
              </Typography>
            </Box>
          ) : (
            <DataTable>
              <thead>
                <tr>
                  <DataTableHeadCell>Date</DataTableHeadCell>
                  <DataTableHeadCell>Method</DataTableHeadCell>
                  <DataTableHeadCell align="right">Amount</DataTableHeadCell>
                  <DataTableHeadCell>Ref</DataTableHeadCell>
                  <DataTableHeadCell>RS</DataTableHeadCell>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <DataTableRow key={p.id ?? p.rsId ?? Math.random()}>
                    <DataTableCell>
                      {p.createdAt
                        ? new Date(p.createdAt).toLocaleDateString('en-AU', {
                            day: '2-digit', month: 'short', year: 'numeric',
                          })
                        : '—'}
                    </DataTableCell>
                    <DataTableCell>{p.paymentMethod || '—'}</DataTableCell>
                    <DataTableCell align="right" paid>
                      {fmtAUD(Number(p.amount) || 0)}
                    </DataTableCell>
                    <DataTableCell>{p.refNum || '—'}</DataTableCell>
                    <DataTableCell>
                      {p.rsId ? (
                        <Link
                          href={`https://techsolvers.repairshopr.com/payments/${p.rsId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontSize: 13 }}
                        >
                          RS
                        </Link>
                      ) : '—'}
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </tbody>
            </DataTable>
          )}
        </Box>

        {/* Line Items */}
        <Box sx={{ ...CARD_SX, p: 2 }}>
          <InvoiceLineItemEditor
            lineItems={invoice.lineItems}
            subtotal={invoice.subtotal}
            tax={invoice.tax}
            total={invoice.total}
            readOnly={isReadOnly}
            onAdd={handleAddLineItem}
            onUpdate={handleUpdateLineItem}
            onDelete={handleDeleteLineItem}
          />
        </Box>
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => !isDeleting && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete invoice #{invoice.number} for {invoice.customerName}?
            This will remove the invoice from RepairShopr and mark it as deleted in SM4RT.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={isDeleting} sx={{ color: '#888' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteInvoice}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : undefined}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send-via-Xero confirmation */}
      <Dialog
        open={confirmSendOpen}
        onClose={() => xeroRunning === null && setConfirmSendOpen(false)}
      >
        <DialogTitle>Send invoice via Xero?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will email invoice <strong>#{invoice.number}</strong> to{' '}
            <strong>{invoice.customerName || 'the linked Xero contact'}</strong> via Xero.
            This action sends a real email and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmSendOpen(false)}
            disabled={xeroRunning === 'send'}
            sx={{ color: '#888' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              xeroRunning === 'send'
                ? <CircularProgress size={14} color="inherit" />
                : <IconSend size={16} stroke={1.5} />
            }
            disabled={xeroRunning !== null}
            onClick={async () => {
              setConfirmSendOpen(false);
              await runXeroAction('send');
            }}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar — generic */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Snackbar — Xero ops (carries severity for clearer success/error feedback) */}
      <Snackbar
        open={xeroSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setXeroSnackbar({ ...xeroSnackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setXeroSnackbar({ ...xeroSnackbar, open: false })}
          severity={xeroSnackbar.severity}
        >
          {xeroSnackbar.message}
        </Alert>
      </Snackbar>

      {checkoutOpen && invoice.rsInvoiceId != null && invoice.rsCustomerId != null && (
        <CheckoutDialog
          open={checkoutOpen}
          onClose={() => setCheckoutOpen(false)}
          invoice={{
            rsInvoiceId: invoice.rsInvoiceId,
            rsCustomerId: invoice.rsCustomerId,
            number: invoice.number,
            balanceDue: Number(invoice.balanceDue) || 0,
          }}
        />
      )}

      {changeCustomerOpen && (
        <ChangeCustomerDialog
          open={changeCustomerOpen}
          invoice={invoice}
          onClose={() => setChangeCustomerOpen(false)}
          onChanged={(updated, warning) => {
            setInvoice(updated);
            if (warning) {
              setTicketWarning(warning);
            } else {
              setSnackbar('Customer changed');
            }
          }}
        />
      )}

      {/* Partial-success warning — invoice moved but the linked ticket didn't.
          Longer-lived + warning severity since it's actionable (fix in RS). */}
      <Snackbar
        open={!!ticketWarning}
        autoHideDuration={8000}
        onClose={() => setTicketWarning(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setTicketWarning(null)}
          severity="warning"
        >
          {ticketWarning}
        </Alert>
      </Snackbar>
    </Box>
  );
};
